import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, Link, Paper, Tooltip, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { isMobile } from "is-mobile";
import { toast } from "react-toastify";
import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { NameCell } from "../../../components/datagrid/NameCell";
import { iniciais } from "../../../components/datagrid/iniciais";
import { ChipButton, ChipGroupLabel, ToolbarDivider } from "../../../components/datagrid/ChipButton";
import type { DataGridColumn, DataGridRow, FilterChip } from "../../../components/datagrid/types";
import { KpiStrip } from "../../../components/kpi-strip";
import { formatarNumero } from "../../../components/kpi-strip/formatar";
import { USER_TYPE_LABEL } from "../../../components/page-base/admin-nav/items";
import useUserStore from "../../../store/user";
import { Container } from "../../sellers/pages/styles";
import {
  ActivityLogsService,
  type ActivityLog,
  type ActivityEvent,
  type ActivitySummary,
} from "../services";
import { descreverAparelho } from "./aparelho";

const EVENTO: Record<ActivityEvent, { label: string; bg: string; fg: string }> = {
  LOGIN: { label: "Login", bg: "#EAF1FB", fg: "#0B4FA8" },
  LOGIN_FALHOU: { label: "Login negado", bg: "#FCE8E6", fg: "#C5221F" },
  ACESSO: { label: "Acesso", bg: "#F1F3F4", fg: "#5F6368" },
  ALTERACAO: { label: "Alteração", bg: "#FEF7E0", fg: "#B06000" },
};

const PERIODOS = [
  { label: "24 h", dias: 1 },
  { label: "7 dias", dias: 7 },
  { label: "30 dias", dias: 30 },
  { label: "180 dias", dias: 180 },
];

const isoDia = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const quando = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

const tomDoPapel = (role?: string | null) =>
  role === "ADMIN" ? "red" : role === "STAFF" ? "green" : role === "CUSTOMER" ? "grey" : "brand";

type Filtros = {
  dias: number;
  event: string;
  userId?: number;
  userLabel?: string;
  ip?: string;
};

export const ActivityLogs = () => {
  const { user } = useUserStore();
  const [filtros, setFiltros] = useState<Filtros>({ dias: 7, event: "" });
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [paginacao, setPaginacao] = useState({ page: 1, pageSize: 50 });
  const [itens, setItens] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState<ActivitySummary | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    if (!user?.token) {
      toast.warn("Sessão expirada. Faça login novamente.");
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - (filtros.dias - 1));
    const [pagina, sumario] = await Promise.all([
      ActivityLogsService.listar(
        {
          de: isoDia(inicio),
          event: filtros.event || undefined,
          userId: filtros.userId,
          ip: filtros.ip,
          q: buscaAplicada || undefined,
          page: paginacao.page - 1,
          size: paginacao.pageSize,
        },
        user.token,
      ),
      ActivityLogsService.resumo(filtros.dias * 24, user.token),
    ]);
    setItens(pagina?.items ?? []);
    setTotal(pagina?.total ?? 0);
    setResumo(sumario);
    setCarregando(false);
  }, [user?.token, filtros, buscaAplicada, paginacao.page, paginacao.pageSize]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const mudarFiltro = useCallback((parcial: Partial<Filtros>) => {
    setFiltros((f) => ({ ...f, ...parcial }));
    setPaginacao((p) => ({ ...p, page: 1 }));
  }, []);

  const filtrarUsuario = useCallback(
    (userId: number | null, label?: string | null) => {
      if (userId == null) return;
      mudarFiltro({ userId, userLabel: label ?? `#${userId}` });
    },
    [mudarFiltro],
  );

  const columns: DataGridColumn[] = [
    { id: "quando", header: "Quando", minWidth: 150 },
    { id: "usuario", header: "Usuário", minWidth: 240 },
    { id: "acao", header: "O que fez", minWidth: 300 },
    { id: "origem", header: "De onde", minWidth: 210 },
    { id: "resultado", header: "Resultado", minWidth: 100, hideOnMobile: true },
  ];

  const rows: DataGridRow[] = useMemo(
    () =>
      itens.map((log) => {
        const ev = EVENTO[log.event] ?? EVENTO.ACESSO;
        const papel = log.userRole ? USER_TYPE_LABEL[log.userRole] ?? log.userRole : "";
        const negado = log.status != null && (log.status === 401 || log.status === 403);
        const erro = log.status != null && log.status >= 400 && !negado;
        return {
          id: log.id,
          render: {
            quando: (
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {quando(log.createdAt)}
              </Typography>
            ),
            usuario: (
              <NameCell
                primaryText={log.userName || log.userEmail || "Desconhecido"}
                secondaryText={[log.userEmail, papel].filter(Boolean).join(" · ")}
                avatar={iniciais(log.userName || log.userEmail || "?")}
                avatarTone={tomDoPapel(log.userRole)}
                onClick={log.userId != null ? () => filtrarUsuario(log.userId, log.userName || log.userEmail) : undefined}
              />
            ),
            acao: (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={ev.label}
                    sx={{ bgcolor: ev.bg, color: ev.fg, fontWeight: 600, height: 22 }}
                  />
                  <Typography variant="body2">{log.description}</Typography>
                </Box>
                {log.profileName && (
                  <Typography variant="caption" color="text.secondary">
                    Perfil: {log.profileName}
                  </Typography>
                )}
                {!log.profileName && log.profileRef && (
                  <Typography variant="caption" color="text.secondary">
                    Perfil: {log.profileRef}
                  </Typography>
                )}
                {log.targetUserId != null && (
                  <Typography variant="caption" color="text.secondary">
                    Usuário afetado: {log.targetUserName || log.targetUserEmail || `#${log.targetUserId}`}
                  </Typography>
                )}
              </Box>
            ),
            origem: (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {log.ip ? (
                    <Tooltip title="Ver só este IP">
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        variant="body2"
                        onClick={() => mudarFiltro({ ip: log.ip ?? undefined })}
                      >
                        {log.ip}
                      </Link>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                  {log.ipNovo && (
                    <Tooltip title="Primeira vez que este usuário aparece com este IP">
                      <Chip size="small" label="IP novo" sx={{ bgcolor: "#FCE8E6", color: "#C5221F", height: 20, fontWeight: 600 }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {descreverAparelho(log.userAgent)}
                  {log.ip && (
                    <>
                      {" · "}
                      <Link href={`https://ipinfo.io/${encodeURIComponent(log.ip)}`} target="_blank" rel="noopener noreferrer">
                        onde fica
                      </Link>
                    </>
                  )}
                </Typography>
              </Box>
            ),
            resultado:
              log.status == null ? (
                <Typography variant="body2" color="text.secondary">
                  {log.event === "LOGIN_FALHOU" ? "Negado" : "OK"}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: negado || erro ? "#C5221F" : "#137333", fontWeight: 600 }}>
                  {negado ? "Sem permissão" : erro ? `Erro ${log.status}` : "OK"}
                </Typography>
              ),
          },
        };
      }),
    [itens, filtrarUsuario, mudarFiltro],
  );

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (filtros.userId != null) {
      chips.push({
        id: "userId",
        label: `Usuário: ${filtros.userLabel}`,
        onDelete: () => mudarFiltro({ userId: undefined, userLabel: undefined }),
      });
    }
    if (filtros.ip) {
      chips.push({ id: "ip", label: `IP: ${filtros.ip}`, onDelete: () => mudarFiltro({ ip: undefined }) });
    }
    if (buscaAplicada) {
      chips.push({
        id: "q",
        label: `Busca: ${buscaAplicada}`,
        onDelete: () => {
          setBusca("");
          setBuscaAplicada("");
        },
      });
    }
    return chips;
  }, [filtros, buscaAplicada, mudarFiltro]);

  const k = resumo?.kpis;
  const variosLugares = resumo?.variosLugares ?? [];
  const periodo = PERIODOS.find((p) => p.dias === filtros.dias)?.label ?? "";

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title="Logs de acesso"
          subtitle="Quem entrou, de qual rede e aparelho, e o que alterou. Logins, tentativas negadas e toda edição ficam guardados por 180 dias."
        />
        <Box sx={{ width: "calc(100% - 64px)", mt: 3 }}>
          <KpiStrip
            aria-label={`Resumo dos últimos ${periodo}`}
            items={[
              { icon: "login", value: formatarNumero(k?.logins ?? 0), label: `logins (${periodo})`, tone: "brand" },
              { icon: "block", value: formatarNumero(k?.falhas ?? 0), label: "logins negados", tone: "red" },
              { icon: "edit_note", value: formatarNumero(k?.alteracoes ?? 0), label: "alterações", tone: "yellow" },
              { icon: "group", value: formatarNumero(k?.usuarios ?? 0), label: "usuários ativos", tone: "green" },
              {
                icon: "devices",
                value: formatarNumero(variosLugares.length),
                label: "contas em 2+ redes",
                tone: variosLugares.length > 0 ? "red" : "grey",
              },
            ]}
          />
        </Box>

        {variosLugares.length > 0 && (
          <Paper
            variant="outlined"
            sx={{ width: "calc(100% - 64px)", mt: 3, p: 2, borderColor: "#F4C7C3", bgcolor: "#FFFBFA", boxSizing: "border-box" }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#C5221F" }}>
              Contas usadas de mais de uma rede ({periodo})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Pode ser só o celular trocando de rede. Se os aparelhos ou as cidades não batem com a pessoa, a senha
              provavelmente foi passada adiante: desative o usuário em Equipe e acessos.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {variosLugares.map((s) => (
                <Box
                  key={s.userId}
                  sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", py: 0.5, borderTop: "1px solid #F1F3F4" }}
                >
                  <Box sx={{ minWidth: 220, flex: "1 1 220px" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.userName || s.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[s.userEmail, s.userRole ? USER_TYPE_LABEL[s.userRole] ?? s.userRole : ""].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ flex: "0 0 auto" }}>
                    <strong>{s.ips}</strong> IPs · <strong>{s.aparelhos}</strong> aparelho(s)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: "2 1 240px", wordBreak: "break-all" }}>
                    {s.listaIps}
                  </Typography>
                  <Link component="button" type="button" variant="body2" onClick={() => filtrarUsuario(s.userId, s.userName || s.userEmail)}>
                    Ver atividade
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        <Box sx={{ width: "calc(100% - 64px)", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}>
          <AppDataGrid
            toolbarChips={
              <>
                <ToolbarDivider />
                <ChipGroupLabel>Período</ChipGroupLabel>
                {PERIODOS.map((p) => (
                  <ChipButton
                    key={p.dias}
                    type="button"
                    $on={filtros.dias === p.dias}
                    aria-pressed={filtros.dias === p.dias}
                    onClick={() => mudarFiltro({ dias: p.dias })}
                  >
                    {p.label}
                  </ChipButton>
                ))}
                <ToolbarDivider />
                <ChipGroupLabel>Tipo</ChipGroupLabel>
                {[
                  { label: "Todos", value: "" },
                  { label: "Alterações", value: "ALTERACAO" },
                  { label: "Logins", value: "LOGIN" },
                  { label: "Negados", value: "LOGIN_FALHOU" },
                  { label: "Acessos", value: "ACESSO" },
                ].map((o) => (
                  <ChipButton
                    key={o.value}
                    type="button"
                    $on={filtros.event === o.value}
                    aria-pressed={filtros.event === o.value}
                    onClick={() => mudarFiltro({ event: o.value })}
                  >
                    {o.label}
                  </ChipButton>
                ))}
              </>
            }
            resultsLabel={`${formatarNumero(total)} registros`}
            columns={columns}
            rows={rows}
            tableLabel="registros"
            searchTerm={busca}
            onSearchTermChange={setBusca}
            searchPlaceholder="Buscar usuário, e-mail, IP, perfil ou ação..."
            onSearchClick={() => {
              setPaginacao((p) => ({ ...p, page: 1 }));
              setBuscaAplicada(busca.trim());
            }}
            filterChips={filterChips}
            filterCount={filterChips.length}
            pagination={{
              page: paginacao.page,
              pageSize: paginacao.pageSize,
              totalCount: total,
              onPageChange: (page) => setPaginacao((p) => ({ ...p, page })),
              onPageSizeChange: (pageSize) => setPaginacao({ page: 1, pageSize }),
            }}
            noDataIcon={<SearchIcon sx={{ fontSize: 48 }} />}
            noDataTitle="Nenhum registro no período"
            noDataDescription="O registro começou com a versão de 01/10/2026; ações anteriores não aparecem aqui."
            isLoading={carregando}
          />
        </Box>
      </Container>
    </Page.Root>
  );
};
